import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalSearchData, Location, Vicinity, Geo } from '@vendasta/listing-products';
import { GMBClaimStatus, Status } from '@vendasta/snapshot';
import { map, Observable, ReplaySubject, Subject, takeUntil, tap } from 'rxjs';
import { offsetToUTCZero } from '../local-seo-graph/local-seo-graph.component';

import {
  buildMapMarkers,
  BusinessPerformance,
  businessPerformance,
  MapMarker,
  SEARCH_RADIUS_LOCAL,
  SEODataSearchRadiusZoomValue,
  SEOPerformance,
} from '../../local-seo';

const selectedMarkerOpacity = 1;
const markerOpacity = 0.6;

@Component({
  selector: 'local-seo-grid',
  templateUrl: 'local-seo-grid.component.html',
  styleUrls: ['local-seo-grid.component.scss'],
  standalone: false,
})
export class LocalSEOGridComponent implements OnInit, OnDestroy {
  @Input() accountGroupId: string;
  @Input() businessData: Location;
  @Input() keyword: string;
  @Input() searchRadius: number;
  @Input() searchDate: Date;
  @Input() set gridPoints(data: LocalSearchData[]) {
    this.localSearches = data;
    this.currentNearMeSearch$$.next(this.localSearches?.find((search) => search.vicinity === Vicinity.VICINITY_C3));
    this.nearMeMarkers = buildMapMarkers(this.localSearches);
    this.localSearchesSubject.next(this.localSearches);
    this.isLoading = false;
  }
  @Input() radiusSetting: number;
  @Input() layout: 'vertical' | 'responsive' = 'responsive';

  localSearches: LocalSearchData[];

  isLoading = true;
  private currentNearMeSearch$$: ReplaySubject<LocalSearchData> = new ReplaySubject<LocalSearchData>(1);
  private readonly conversionFactor = 1.60934;
  protected currentNearMeSearch$: Observable<LocalSearchData> = this.currentNearMeSearch$$.asObservable();
  protected nearMeMarkers: MapMarker[];

  protected mapUnits: string;
  protected translatedMapUnitsKMS: string;

  private destroy$$ = new Subject<void>();
  private translatedMapUnitsKMS$: Observable<string>;
  private localSearchesSubject = new ReplaySubject<LocalSearchData[]>(1);
  protected SEOPerformance = SEOPerformance;

  protected businessPerformance = businessPerformance;

  protected get Vicinity(): typeof Vicinity {
    return Vicinity;
  }

  protected get Status(): typeof Status {
    return Status;
  }

  protected get GMBClaimStatus(): typeof GMBClaimStatus {
    return GMBClaimStatus;
  }

  protected get CenterPoint(): Geo {
    const center = this.localSearches?.find((search) => search.vicinity === Vicinity.VICINITY_C3);
    if (!center) {
      return this.businessData.location;
    }
    return center?.searchLocation;
  }

  protected get MainBusinessPerformance(): BusinessPerformance {
    const center = this.localSearches?.find((search) => search.vicinity === Vicinity.VICINITY_C3);
    const mainBusiness = center?.results.find((result) => result.isMainBusiness);
    return this.businessPerformance(+mainBusiness.rank);
  }

  protected get MainBusinessRank(): number {
    const center = this.localSearches?.find((search) => search.vicinity === Vicinity.VICINITY_C3);
    const mainBusiness = center?.results.find((result) => result.isMainBusiness);
    return +mainBusiness.rank;
  }

  constructor(private translate: TranslateService) {}

  protected getZoomLevel(radius: number): number {
    return SEODataSearchRadiusZoomValue[radius ?? SEARCH_RADIUS_LOCAL];
  }

  ngOnInit(): void {
    this.initializeMapUnits();
  }

  private initializeMapUnits(): void {
    this.mapUnits =
      this.getLocalStorage('mapUnits') ?? this.translate.instant('LOCAL_SEO.SELECT_LOCAL_SEARCH_RADIUS.MILES');
    this.translatedMapUnitsKMS$ = this.translate.get('LOCAL_SEO.SELECT_LOCAL_SEARCH_RADIUS.KMS').pipe(
      takeUntil(this.destroy$$),
      tap((translation: string) => (this.translatedMapUnitsKMS = translation)),
    );
  }

  ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
    this.localSearchesSubject.complete();
    this.currentNearMeSearch$$.complete();
  }

  protected onMarkerClick(index: number): void {
    this.currentNearMeSearch$$.next(this.localSearches[index]);
    this.setNearMeSelectedMarker(index);
  }

  protected offsetUTC(date: Date): Date {
    return offsetToUTCZero(date.toString());
  }

  private getLocalStorage(key: string): string | null {
    return localStorage.getItem(`${this.accountGroupId}-${key}`);
  }

  private setNearMeSelectedMarker(index: number): void {
    this.nearMeMarkers.forEach((m) => {
      if (m.icon.fillOpacity === 1) {
        m.icon = { ...m.icon, fillOpacity: markerOpacity };
      }
    });
    this.nearMeMarkers[index].icon = { ...this.nearMeMarkers[index].icon, fillOpacity: selectedMarkerOpacity };
  }

  protected getConvertedRadiusValue(radiusValue: number, mapUnits: string, kilometersTranslationKey: string): number {
    if (mapUnits === kilometersTranslationKey) {
      const kilometers = radiusValue * this.conversionFactor;
      return Math.round(kilometers);
    }
    return radiusValue;
  }

  protected getConvertedRadiusValue$(radiusValue: number, mapUnits: string): Observable<number> {
    return this.translatedMapUnitsKMS$.pipe(
      map((kilometersTranslationKey) => {
        if (mapUnits === kilometersTranslationKey) {
          const kilometers = radiusValue * this.conversionFactor;
          return Math.round(kilometers);
        }
        return radiusValue;
      }),
    );
  }
}
